import type {
  InventoryResource,
  Account,
  FileSnapshot,
} from '@repo/api-gw-sdk';
import { Provider } from '@repo/api-gw-sdk';

import useBackupVaults from '@/data/vaults/useBackupVaults';

import type { RestoreGenericStorageProps } from './bucketSelection';
import { FileConfiguration } from './steps/fileConfiguration';
import { SelectTargetAccountStep } from './steps/selectTargetAccount';

import type { Step } from '../wizard/Step';
import { Wizard } from '../wizard/Wizard';

export interface RestoreFileState extends RestoreGenericStorageProps {
  actionType: string;
  resourceId?: string;
  fileSnapshot: FileSnapshot;
  vaultId: string;
  supportedCloudProviders: Provider[];
  restoreAccount?: Account;
  files: { path: string; isDirectory: boolean }[];
}

const restoreFileFlow: Step<RestoreFileState> = {
  name: 'SelectTargetAccountStep',
  Component: SelectTargetAccountStep,
  next: [
    {
      name: 'FileConfiguration',
      Component: FileConfiguration,
    },
  ],
};

interface Props {
  path: string;
  fileSnapshot: FileSnapshot;
  vaultId: string;
  resource: InventoryResource;
  onClose: () => void;
}

export const supportedCrossCloudRestores: Record<Provider, Provider[]> = {
  [Provider.Aws]: [Provider.Aws],
  [Provider.Azure]: [Provider.Azure, Provider.Aws, Provider.Gcp],
  [Provider.Gcp]: [Provider.Gcp],
  [Provider.MongoAtlas]: [], // No vaults in Atlas
  [Provider.ProviderUnspecified]: [],
};

export const RestoreFileWizard = (props: Props) => {
  const { vaults } = useBackupVaults();

  const files = [{ path: props.path, isDirectory: false }];
  const cloudProvider = vaults.find((v) => v.id === props.vaultId)
    ?.vaultAttributes.cloudProvider;

  return (
    <Wizard<RestoreFileState>
      onFinish={async () => true}
      onAbort={props.onClose}
      flow={[restoreFileFlow]}
      initialState={{
        files,
        actionType: 'file',
        fileSnapshot: props.fileSnapshot,
        vaultId: props.vaultId,
        resourceId: props.resource.id,
        restoreMode: 'select',
        S3: {},
        GCS: {},
        StorageAccount: {},
        supportedCloudProviders:
          (cloudProvider && supportedCrossCloudRestores[cloudProvider]) ?? [],
      }}
      title={'Restore File'}
      stepperLabels={['Restore Account', 'Configuration']}
    />
  );
};
